import React, { useEffect, useState, useRef } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { CatalogueMainHeader } from '../components/catalogue/CatalogueMainHeader';
import { AudioUploadSlot, SoundworldModal } from '../components/catalogue';
import { Select } from '../components/ui/select';
import { Music2, Layers, Download, RefreshCcw } from 'lucide-react';
import { SoundSignalVisualizer, SoundSignalVisualizerRef } from '../components/ui/SoundSignalVisualizer';
import { API_CONFIG, getAuthHeadersWithoutCredentials } from '../constants/api';
import toast from 'react-hot-toast';

import { Project } from '../types/project';

export const ProjectDetailsPage = ({ username }) => {
    const { isAuthenticated, isLoading } = useAuth0();
    const { projectId } = useParams();
    const navigate = useNavigate();
    const locationPath = useLocation();
    const audioRef = useRef<SoundSignalVisualizerRef>(null);

    const [project, setProject] = useState<Project | null>(null);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [stemsLoading, setStemsLoading] = useState(false);
    const [vocalsFile, setVocalsFile] = useState(null);
    const [bassFile, setBassFile] = useState(null);
    const [midsFile, setMidsFile] = useState(null);
    const [drumsFile, setDrumsFile] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [soundworld, setSoundworld] = useState('ChillPianoAging');
    const [startsOnBeat, setStartsOnBeat] = useState(1);
    const [isLoop, setIsLoop] = useState(false);
    const [sectionType, setSectionType] = useState('verse');

    useEffect(() => {
        if (!isAuthenticated && !isLoading) {
            navigate('/');
            return;
        }
    }, [isAuthenticated, isLoading, navigate]);

    const getStemFilename = (path) => path?.split('/').pop() || null;

    const createDummyFile = (name) =>
        new File(["dummy"], name, { type: "audio/mpeg", lastModified: 0 });

    const showError = (msg) => {
        toast.error(msg);
    };

    const uploadStemToProject = async (file, role) => {
        if (!projectId || !file || (file.size === 5 && file.lastModified === 0)) return;

        const formData = new FormData();
        formData.append('file', file);

        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.UPLOAD}?project_id=${projectId}&role=${role}`, {
                method: 'POST',
                headers: {
                    Authorization: getAuthHeadersWithoutCredentials().Authorization
                },
                body: formData
            });

            if (!res.ok) {
                throw new Error(`Failed to upload ${role} stem`);
            }
        } catch (err) {
            showError(`Failed to upload ${role} stem.`);
        }
    };

    const deleteStemFromProject = async (role) => {
        if (!projectId) return;

        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.DELETE_FILE}?project_id=${projectId}&role=${role}`, {
                method: 'DELETE',
                headers: getAuthHeadersWithoutCredentials(),
            });

            if (!res.ok) {
                throw new Error(`Failed to delete ${role} stem`);
            }

            const fileSetters = {
                vocals: setVocalsFile,
                bass: setBassFile,
                mids: setMidsFile,
                drums: setDrumsFile
            };
            fileSetters[role]?.(null);
        } catch (err) {
            showError(`Failed to delete ${role} stem.`);
        }
    };

    useEffect(() => {
        const stemPairs = [
            [vocalsFile, 'LEAD'],
            [bassFile, 'BASS'],
            [midsFile, 'CHORDS'],
            [drumsFile, 'DRUMS']
        ];

        stemPairs.forEach(([file, role]) => {
            if (file) uploadStemToProject(file, role);
        });
    }, [vocalsFile, bassFile, midsFile, drumsFile]);

    const renderStems = async (projectId: string) => {
        try {
            const params = new URLSearchParams({
                project_id: projectId
            });

            const sessionId = localStorage.getItem('session_id');
            if (sessionId) {
                params.append('session_id', sessionId);
            }

            const res = await fetch(
                `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.RENDER_STEMS}?${params.toString()}`,
                {
                    method: 'GET',
                    headers: getAuthHeadersWithoutCredentials(),
                }
            );

            if (!res.ok) {
                throw new Error('Failed to render stems');
            }

            return await res.json();
        } catch (err) {
            console.error('Failed to render stems:', err);
            return null;
        }
    };

    const fetchProject = async () => {
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PROJECTS}`, {
                headers: getAuthHeadersWithoutCredentials(),
            });

            if (!res.ok) {
                throw new Error('Failed to fetch projects');
            }

            const data = await res.json();
            const found = data.find(p => p.project_id === projectId);
            if (!found) throw new Error('Project not found');

            setProject(found);
            setStartsOnBeat(found?.first_beat_override || 1);
            setSoundworld(found.soundworld || 'ChillPianoAging');
            setIsLoop(found.render_mode === 'loop');
            setSectionType(found.section_type_override || 'verse');

            // Handle stems if they exist
            if (found.retained_stem_paths) {
                const stems = found.retained_stem_paths;
                if (stems.LEAD) setVocalsFile(createDummyFile(getStemFilename(stems.LEAD)));
                if (stems.BASS) setBassFile(createDummyFile(getStemFilename(stems.BASS)));
                if (stems.CHORDS) setMidsFile(createDummyFile(getStemFilename(stems.CHORDS)));
                if (stems.DRUMS) setDrumsFile(createDummyFile(getStemFilename(stems.DRUMS)));
            }

        } catch (err) {
            showError('Error loading project');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProject();
    }, [projectId]);

    // Listen for WebSocket events
    useEffect(() => {
        const handleAudioReady = (event) => {
            const { projectId: eventProjectId } = event.detail;
            if (eventProjectId === projectId) {
                setGenerating(false);
                fetchProject();
            }
        };

        const handleStemsReady = (event) => {
            const { projectId: eventProjectId, url } = event.detail;
            if (eventProjectId === projectId) {
                setGenerating(false);
                setStemsLoading(false);
                toast.success('Stems are ready!');

                if (url) {
                    handleDownload(url, 'project-stems.zip');
                }

                fetchProject();
            }
        };

        const handleJobError = (event) => {
            const { message } = event.detail;
            showError(`Job failed: ${message}`);
            setGenerating(false);
            setStemsLoading(false);
        };

        window.addEventListener('audioReady', handleAudioReady);
        window.addEventListener('stemsReady', handleStemsReady);
        window.addEventListener('jobError', handleJobError);

        return () => {
            window.removeEventListener('audioReady', handleAudioReady);
            window.removeEventListener('stemsReady', handleStemsReady);
            window.removeEventListener('jobError', handleJobError);
        };
    }, [projectId]);

    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
            }
        };
    }, [locationPath.pathname]);

    const pollForAudioReady = (targetProjectId) => {
        const interval = setInterval(async () => {
            try {
                const res = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.HAS_AUDIO}?project_id=${targetProjectId}`, {
                    headers: getAuthHeadersWithoutCredentials(),
                });

                if (!res.ok) {
                    throw new Error('Failed to check audio status');
                }

                const isReady = await res.json();
                if (isReady === true) {
                    clearInterval(interval);
                    setGenerating(false);
                    fetchProject();
                }
            } catch (err) {
                // Silent error handling
            }
        }, 2000);
    };

    const handleExecute = async () => {
        if (!project) return;
        setGenerating(true);

        try {
            const params = new URLSearchParams({
                project_id: project.project_id,
                soundworld: soundworld,
                first_beat_override: startsOnBeat.toString()
            });

            const sessionId = localStorage.getItem('session_id');
            if (sessionId) {
                params.append('session_id', sessionId);
            }

            if (isLoop) {
                params.append('section_type_override', sectionType);
            }

            const res = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.EXECUTE}?${params.toString()}`, {
                headers: getAuthHeadersWithoutCredentials(),
            });

            if (!res.ok) {
                throw new Error('Failed to execute project');
            }

            const result = await res.json();

            if (result === 'OK') {
                // WebSocket will handle completion
                // Note: WebSocket is now global, so no need to check connection here
            } else {
                showError('Error during generation');
                setGenerating(false);
            }
        } catch (err) {
            showError('Error calling execute');
            setGenerating(false);
        }
    };

    const handleDownload = async (endpoint, filename) => {
        try {
            const res = await fetch(endpoint, {
                headers: getAuthHeadersWithoutCredentials(),
            });

            if (!res.ok) {
                throw new Error(`Failed to download ${filename}`);
            }

            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            showError(`Failed to download ${filename}`);
        }
    };

    const handleGetStems = async () => {
        if (!project) {
            showError('No project available');
            return;
        }

        setStemsLoading(true);
        try {
            console.log('Starting stems rendering for projectId:', project.project_id);

            await renderStems(project.project_id);
            toast.success('Stems rendering started! You will be notified when ready.');
        } catch (error) {
            console.error('Error starting stems rendering:', error);
            showError('Failed to start stems rendering');
            setStemsLoading(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin"></div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return null;
    }

    if (loading) return <p className="p-10 text-center">Loading project...</p>;
    if (!project) return <p className="p-10 text-center text-red-500">Project not found</p>;

    return (
        <div className="h-screen overflow-hidden bg-background">
            <CatalogueMainHeader username={ username } backButton />

            <div className="h-full overflow-y-auto px-6 py-6">
                <div className="flex flex-col items-center justify-center min-h-full">
                    { project.tags?.includes('derivative') && (
                        <p className="text-2xl mb-2 font-medium text-card-foreground">Project: { project.name }</p>
                    ) }
                    { project.tags?.includes('soundworld') && (
                        <p className="text-2xl mb-2 font-medium text-card-foreground">Soundworld Project: { project.name }</p>
                    ) }

                    <p className="text-sm text-muted-foreground mb-6">{ project.description }</p>

                    <div className="flex justify-center items-center gap-6 mb-4 flex-wrap">
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-card-foreground">Soundworld:</span>
                            <button
                                onClick={ () => setShowModal(true) }
                                className="bg-muted px-3 py-1 rounded text-sm cursor-pointer hover:bg-accent transition-colors text-muted-foreground"
                            >
                                { soundworld || 'Select' }
                            </button>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-card-foreground">Starts on beat:</span>
                            <Select
                                value={ startsOnBeat.toString() }
                                onChange={ (value) => setStartsOnBeat(parseInt(value)) }
                                options={ [
                                    { value: '1', label: '1' },
                                    { value: '2', label: '2' },
                                    { value: '3', label: '3' },
                                    { value: '4', label: '4' }
                                ] }
                                placeholder="Select beat"
                                className="w-20"
                            />
                        </div>


                        { project.render_mode === "loop" && (
                            <>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-card-foreground">Loop mode:</span>
                                    <input
                                        type="checkbox"
                                        checked={ isLoop }
                                        onChange={ (e) => setIsLoop(e.target.checked) }
                                        className="rounded border-border cursor-pointer"
                                    />
                                </div>

                                { isLoop && (
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium text-card-foreground">Section type:</span>
                                        <Select
                                            value={ sectionType }
                                            onChange={ (value) => setSectionType(value) }
                                            options={ [
                                                { value: 'verse', label: 'Verse' },
                                                { value: 'chorus', label: 'Chorus' },
                                                { value: 'bridge', label: 'Bridge' },
                                                { value: 'intro', label: 'Intro' },
                                                { value: 'outro', label: 'Outro' }
                                            ] }
                                            placeholder="Select section type"
                                            className="w-24"
                                        />
                                    </div>
                                ) }
                            </>
                        ) }
                    </div>

                    { project.tags?.includes('derivative') && (
                        <div className="flex flex-wrap gap-4 mt-6 mb-6 justify-center">
                            <AudioUploadSlot label="Vocals" file={ vocalsFile } setFile={ setVocalsFile } onDelete={ () => deleteStemFromProject('LEAD') } />
                            <AudioUploadSlot label="Bass" file={ bassFile } setFile={ setBassFile } onDelete={ () => deleteStemFromProject('BASS') } />
                            <AudioUploadSlot label="Mids" file={ midsFile } setFile={ setMidsFile } onDelete={ () => deleteStemFromProject('CHORDS') } />
                            <AudioUploadSlot label="Drums" file={ drumsFile } setFile={ setDrumsFile } onDelete={ () => deleteStemFromProject('DRUMS') } />
                        </div>
                    ) }

                    <div className="bg-card border border-border rounded-xl w-full max-w-4xl p-6 shadow-sm">
                        <h2 className="text-lg font-semibold text-center mb-4 text-card-foreground">Generated Track</h2>

                        { (project.audio_url) ? (
                            <SoundSignalVisualizer
                                ref={ audioRef }
                                audioUrl={ project.audio_url }
                                barsCount={ 8 }
                                showBpmControls={ false }
                                controlsExtra={
                                    <>
                                        <button
                                            onClick={ async () => {
                                                try {
                                                    const res = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.DERIVATIVE_GET_MIDI_URL}?project_id=${project.project_id}`, {
                                                        headers: getAuthHeadersWithoutCredentials(),
                                                    });
                                                    if (!res.ok) throw new Error('Failed to get MIDI URL');
                                                    const data = await res.json();
                                                    if (data?.url) {
                                                        await handleDownload(data.url, 'project-midi.mid');
                                                    } else {
                                                        showError('MIDI URL not available');
                                                    }
                                                } catch (e) {
                                                    showError('Failed to get MIDI file');
                                                }
                                            } }
                                            className="btn-primary px-5 py-3 text-sm font-medium flex items-center gap-2"
                                        >
                                            <Music2 size={ 16 } /> Get MIDI
                                        </button>
                                        <button
                                            onClick={ handleGetStems }
                                            disabled={ stemsLoading }
                                            className="btn-primary px-5 py-3 text-sm font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            { stemsLoading ? (
                                                <>
                                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                    Rendering...
                                                </>
                                            ) : (
                                                <>
                                                    <Layers size={ 16 } /> Get Stems
                                                </>
                                            ) }
                                        </button>
                                        <a
                                            href={ project.audio_url }
                                            download
                                            className="btn-primary px-5 py-3 text-sm font-medium flex items-center gap-2"
                                        >
                                            <Download size={ 16 } /> Get mp3
                                        </a>
                                        <button
                                            onClick={ handleExecute }
                                            disabled={ generating }
                                            className={ `px-5 py-3 rounded-lg text-sm font-medium flex items-center gap-2 ${generating
                                                ? 'bg-muted text-muted-foreground cursor-wait'
                                                : 'btn-primary'
                                                }` }
                                        >
                                            { generating ? (
                                                <svg className="w-4 h-4 animate-spin text-muted-foreground" viewBox="0 0 24 24" fill="none">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                                                </svg>
                                            ) : (
                                                <RefreshCcw size={ 16 } />
                                            ) }
                                            { generating ? 'Generating...' : 'Generate Again' }
                                        </button>
                                    </>
                                }
                            />
                        ) : (
                            <p className="text-sm text-muted-foreground text-center mb-4">No audio available</p>
                        ) }
                    </div>
                </div>
            </div>

            { showModal && (
                <SoundworldModal
                    onClose={ () => setShowModal(false) }
                    onSelect={ (sw) => {
                        setSoundworld(sw);
                        setShowModal(false);
                    } }
                />
            ) }

        </div>
    );
};
